import {
    ChangeEvent,
    FC,
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react';
import { Link, router } from '@inertiajs/react';

import { Button } from '@/Components/Button';
import { InputLabel } from '@/Components/InputLabel';
import TextInput, { TextInputHandle } from '@/Components/TextInput';
import { useToast } from '@/hook/useToast';
import { AppModalProps } from '@/types/modal.type';
import { ModalView } from '@/Components/ModalView';
import { FlickCherriesSvg } from '@/Components/Icons/FlickCherriesSvg';
import { PageProps } from '@/types';
import { FlickAnimation } from '@/Components/FlickAnimation';
import { FruitBlueberrySvg } from '@/Components/Icons/FruitBlueberrySvg';
import { CherrySvg } from '@/Components/Icons/CherrySvg';

type DuplicateFlowModalProps = AppModalProps<{
    flowId: number;
    flowName: string;
}>;

export const DuplicateFlowModal: FC<DuplicateFlowModalProps> = ({
    payload,
    onClose,
}) => {
    const [value, setValue] = useState<string>(
        `Copy of Flow ${payload.flowName || ''}`,
    );

    const inputRef = useRef<TextInputHandle>(null);

    const focusInput = () => {
        inputRef.current?.focus();
    };

    const { showToast, hideToast } = useToast();

    const onChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        setValue(event.target.value);
    }, []);

    const onDuplicate = useCallback(() => {
        if (import.meta.env.DEV) {
            console.log('flowName', value);
        }

        onClose();

        return new Promise<null>((resolve, reject) => {
            hideToast();

            router.post(
                route('flows.copy', {
                    flowId: payload.flowId,
                }),
                {
                    name: value,
                },
                {
                    // @ts-expect-error
                    onSuccess: (
                        res: PageProps<{
                            props: {
                                flow_copy_id: number;
                            };
                        }>,
                    ) => {
                        showToast({
                            autoClose: false,
                            variant: 'success',
                            label: (
                                <>
                                    Flow duplicated successfully. Go to{' '}
                                    <a
                                        target="_blank"
                                        className="text-brand-teal"
                                        href={route('flows.show', {
                                            id: res.props.flow_copy_id,
                                        })}
                                        onClick={() => {
                                            hideToast();
                                        }}>
                                        {value}
                                    </a>
                                    .
                                </>
                            ),
                        });

                        resolve(null);
                    },
                    onError: (errors) => {
                        Object.keys(errors).forEach((errorKey) => {
                            showToast({
                                variant: 'danger',
                                label: errors[errorKey],
                                autoClose: false,
                            });
                        });

                        reject(errors);
                    },
                },
            );
        });
    }, [payload.flowId, value, onClose]);

    return (
        <ModalView
            onAnimationStart={focusInput}
            maxWidth="sm"
            onClose={onClose}
            onSubmit={onDuplicate}
            contentContainerClassName="py-6 px-8 text-brand-ocean flex flex-col justify-center items-center">
            <div className="relative min-w-60 h-24 flex items-center justify-center">
                <FlickAnimation className="absolute z-10 top-0 left-14" />
                <div className="absolute bottom-5 right-20">
                    <CherrySvg />
                </div>
            </div>
            <InputLabel
                htmlFor="flow-name"
                value="Name your new Flow"
                className="w-full text-left mb-2 text-heading-five !font-bold text-brand-ocean"
            />
            <TextInput
                id="flow-name"
                ref={inputRef}
                placeholder="Flow Name"
                className="w-full mb-2"
                value={value}
                onChange={onChange}
            />
            <div className="w-full !font-normal text-heading-five text-left text-gray-80 mb-2">
                This title isn’t visible to website visitors
            </div>
            <div className="mt-2 w-full flex items-center justify-center space-x-3">
                <Button
                    className="w-full h-9"
                    variant="default"
                    onClick={onClose}>
                    Cancel
                </Button>
                <Button
                    className="w-full h-9"
                    variant="primary"
                    onClick={onDuplicate}>
                    Duplicate Flow
                </Button>
            </div>
        </ModalView>
    );
};
